import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { BackgroundSoundListItemResponseDto } from '@shared/services/apiService/apiService';

import { useStyles } from './styles';
import SoundSelectRow from './SoundSelectRow';
import SoundVolumeRow from './SoundVolumeRow';
import ThinkingSoundCard from './ThinkingSoundCard';
import { useSoundPreview } from '../hooks/useSoundPreview';
import {
  AmbienceSettingsDraft,
  createAmbientSoundDraft,
  createThinkingSoundDraft,
  getSoundDefaultVolume,
  getThinkingProbabilityTotal,
} from '../utils/ambienceSettings.utils';

type AmbienceSettingsFormProps = {
  isLoadingSounds: boolean;
  sounds: BackgroundSoundListItemResponseDto[];
  value: AmbienceSettingsDraft | null;
  onChange: (value: AmbienceSettingsDraft) => void;
  ambientDisabled: boolean;
  thinkingDisabled: boolean;
  ambientNotice?: string | null;
  thinkingNotice?: string | null;
  showOverrideControls?: boolean;
  rootSx?: SxProps<Theme>;
  hasAttemptedSave?: boolean;
};

export const AmbienceSettingsForm: React.FC<AmbienceSettingsFormProps> = ({
  isLoadingSounds,
  sounds,
  value,
  onChange,
  ambientDisabled,
  thinkingDisabled,
  ambientNotice,
  thinkingNotice,
  showOverrideControls = true,
  rootSx,
  hasAttemptedSave = false,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { playingPreviewKeys, stopPreview, togglePreview, updatePreviewVolume } = useSoundPreview();
  const [hasProbabilityBlurred, setHasProbabilityBlurred] = useState(false);

  const ambientSounds = useMemo(() => sounds.filter((sound) => sound.type === 'ambient'), [sounds]);
  const thinkingSounds = useMemo(
    () => sounds.filter((sound) => sound.type === 'thinking'),
    [sounds],
  );

  const selectedThinkingIds = useMemo(
    () => value?.thinkingSounds.map((item) => item.soundId) ?? [],
    [value],
  );

  const thinkingProbabilityTotal = useMemo(
    () => (value ? getThinkingProbabilityTotal(value) : 0),
    [value],
  );
  const hasProbabilityError = thinkingProbabilityTotal > 100;
  const shouldShowProbabilityErrors =
    hasProbabilityError && (hasProbabilityBlurred || hasAttemptedSave);
  const ambientOverrideInactive = showOverrideControls && !value?.overrideAmbientSound;
  const thinkingOverrideInactive = showOverrideControls && !value?.overrideThinkingSounds;
  const ambientPreviewKey = 'ambient';

  useEffect(() => {
    if (!value || !hasProbabilityError) {
      setHasProbabilityBlurred(false);
    }
  }, [hasProbabilityError, value]);

  const ambienceLabels = useMemo(
    () => ({
      sound: t('campaignPage.ambienceSettings.sound'),
      volume: t('campaignPage.ambienceSettings.volume'),
      none: t('campaignPage.ambienceSettings.none'),
    }),
    [t],
  );

  const updateValue = (updater: (draft: AmbienceSettingsDraft) => AmbienceSettingsDraft) => {
    if (!value) {
      return;
    }

    onChange(updater(value));
  };

  if (isLoadingSounds || !value) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '240px',
          width: '100%',
        }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={classes.root} sx={rootSx}>
      <Box
        className={`${classes.section} ${ambientOverrideInactive ? classes.sectionMuted : ''}`.trim()}>
        <Typography className={classes.sectionTitle}>
          {t('campaignPage.ambienceSettings.backgroundTitle')}
        </Typography>
        {showOverrideControls ? (
          <FormControlLabel
            control={
              <Checkbox
                checked={value.overrideAmbientSound}
                disabled={ambientDisabled}
                onChange={(event) =>
                  updateValue((draft) => ({
                    ...draft,
                    overrideAmbientSound: event.target.checked,
                  }))
                }
              />
            }
            label={t('campaignPage.ambienceSettings.overrideAmbient')}
          />
        ) : null}
        {ambientNotice ? (
          <Typography variant="body2" className={classes.disabledNotice}>
            {ambientNotice}
          </Typography>
        ) : null}
        {ambientOverrideInactive ? (
          <Typography variant="body2" className={classes.sectionHint}>
            {t('campaignPage.ambienceSettings.overrideAmbientHint')}
          </Typography>
        ) : null}
        {value.ambientSound?.missing ? (
          <Box>
            <Typography variant="body2" className={classes.disabledNotice}>
              {t('campaignPage.ambienceSettings.missingSoundWarning')}
            </Typography>
            <Button
              variant="text"
              size="small"
              sx={{ px: 0, minWidth: 'auto' }}
              disabled={ambientDisabled || !ambientSounds.length}
              onClick={() =>
                updateValue((draft) => ({
                  ...draft,
                  ambientSound: createAmbientSoundDraft(ambientSounds[0]),
                }))
              }>
              {t('campaignPage.ambienceSettings.replaceMissingSound')}
            </Button>
          </Box>
        ) : null}

        <SoundSelectRow
          label={ambienceLabels.sound}
          disabled={ambientDisabled || ambientOverrideInactive}
          value={value.ambientSound?.soundId ?? ''}
          options={ambientSounds}
          missingValueLabel={
            value.ambientSound?.missing
              ? t('campaignPage.ambienceSettings.missingSoundOption')
              : undefined
          }
          allowNone
          noneLabel={ambienceLabels.none}
          isPlaying={Boolean(playingPreviewKeys[ambientPreviewKey])}
          canPreview={Boolean(
            ambientSounds.find((sound) => sound.id === value.ambientSound?.soundId)?.fileUrl,
          )}
          previewButtonLabel={
            playingPreviewKeys[ambientPreviewKey]
              ? t('campaignPage.ambienceSettings.stopPreview')
              : t('campaignPage.ambienceSettings.playPreview')
          }
          onChange={(nextSoundId) => {
            stopPreview(ambientPreviewKey);
            updateValue((draft) => ({
              ...draft,
              ambientSound: nextSoundId
                ? createAmbientSoundDraft(
                    ambientSounds.find((sound) => sound.id === nextSoundId) ?? null,
                  )
                : null,
            }));
          }}
          onTogglePreview={() =>
            togglePreview(
              ambientPreviewKey,
              ambientSounds.find((sound) => sound.id === value.ambientSound?.soundId)?.fileUrl,
              value.ambientSound?.volume ?? 0,
            )
          }
        />

        <SoundVolumeRow
          label={ambienceLabels.volume}
          disabled={ambientDisabled || ambientOverrideInactive}
          value={value.ambientSound?.volume ?? 0}
          hasSound={Boolean(value.ambientSound?.soundId)}
          muteButtonLabel={
            value.ambientSound?.volume
              ? t('campaignPage.ambienceSettings.muteSound')
              : t('campaignPage.ambienceSettings.unmuteSound')
          }
          onChange={(nextVolume) => {
            updatePreviewVolume(ambientPreviewKey, nextVolume);
            updateValue((draft) => ({
              ...draft,
              ambientSound: draft.ambientSound
                ? {
                    ...draft.ambientSound,
                    volume: nextVolume,
                    previousVolume: nextVolume > 0 ? nextVolume : draft.ambientSound.previousVolume,
                  }
                : null,
            }));
          }}
          onToggleMute={() =>
            updateValue((draft) => {
              if (!draft.ambientSound) {
                return draft;
              }

              const selectedSound = ambientSounds.find(
                (sound) => sound.id === draft.ambientSound?.soundId,
              );
              const nextVolume =
                draft.ambientSound.volume > 0
                  ? 0
                  : draft.ambientSound.previousVolume || getSoundDefaultVolume(selectedSound);

              updatePreviewVolume(ambientPreviewKey, nextVolume);

              return {
                ...draft,
                ambientSound: {
                  ...draft.ambientSound,
                  volume: nextVolume,
                  previousVolume:
                    draft.ambientSound.volume > 0
                      ? draft.ambientSound.volume
                      : draft.ambientSound.previousVolume || getSoundDefaultVolume(selectedSound),
                },
              };
            })
          }
        />

        {!ambientSounds.length ? (
          <Typography variant="body2" className={classes.helperText}>
            {t('campaignPage.ambienceSettings.ambientEmpty')}
          </Typography>
        ) : null}
      </Box>

      <Box
        className={`${classes.section} ${thinkingOverrideInactive ? classes.sectionMuted : ''}`.trim()}>
        <Typography className={classes.sectionTitle}>
          {t('campaignPage.ambienceSettings.thinkingTitle')}
        </Typography>
        {showOverrideControls ? (
          <FormControlLabel
            control={
              <Checkbox
                checked={value.overrideThinkingSounds}
                disabled={thinkingDisabled}
                onChange={(event) =>
                  updateValue((draft) => ({
                    ...draft,
                    overrideThinkingSounds: event.target.checked,
                  }))
                }
              />
            }
            label={t('campaignPage.ambienceSettings.overrideThinking')}
          />
        ) : null}
        {thinkingNotice ? (
          <Typography variant="body2" className={classes.disabledNotice}>
            {thinkingNotice}
          </Typography>
        ) : null}
        {thinkingOverrideInactive ? (
          <Typography variant="body2" className={classes.sectionHint}>
            {t('campaignPage.ambienceSettings.overrideThinkingHint')}
          </Typography>
        ) : null}
        <Typography
          variant="body2"
          className={
            hasProbabilityError ? classes.probabilitySummaryError : classes.probabilitySummary
          }>
          {t('campaignPage.ambienceSettings.probabilityTotalLabel', {
            total: thinkingProbabilityTotal,
          })}
        </Typography>

        {value.thinkingSounds.map((thinkingSound) => (
          <ThinkingSoundCard
            key={thinkingSound.clientId}
            sound={thinkingSound}
            sounds={thinkingSounds}
            selectedThinkingIds={selectedThinkingIds}
            disabled={thinkingDisabled || thinkingOverrideInactive}
            shouldShowProbabilityErrors={shouldShowProbabilityErrors}
            onProbabilityBlur={() => setHasProbabilityBlurred(true)}
            onChange={(updater) =>
              updateValue((draft) => ({
                ...draft,
                thinkingSounds: draft.thinkingSounds.map((item) =>
                  item.clientId === thinkingSound.clientId ? updater(item) : item,
                ),
              }))
            }
            onRemove={() =>
              updateValue((draft) => ({
                ...draft,
                thinkingSounds: draft.thinkingSounds.filter(
                  (item) => item.clientId !== thinkingSound.clientId,
                ),
              }))
            }
            onStopPreview={stopPreview}
            onTogglePreview={togglePreview}
            onUpdatePreviewVolume={updatePreviewVolume}
            isPreviewPlaying={Boolean(playingPreviewKeys[`thinking-${thinkingSound.clientId}`])}
            previewButtonLabel={
              playingPreviewKeys[`thinking-${thinkingSound.clientId}`]
                ? t('campaignPage.ambienceSettings.stopPreview')
                : t('campaignPage.ambienceSettings.playPreview')
            }
            muteButtonLabel={
              thinkingSound.volume
                ? t('campaignPage.ambienceSettings.muteSound')
                : t('campaignPage.ambienceSettings.unmuteSound')
            }
          />
        ))}

        {!thinkingSounds.length ? (
          <Typography variant="body2" className={classes.helperText}>
            {t('campaignPage.ambienceSettings.thinkingEmpty')}
          </Typography>
        ) : null}

        {thinkingSounds.length > value.thinkingSounds.length ? (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            className={classes.addButton}
            disabled={thinkingDisabled || thinkingOverrideInactive}
            onClick={() => {
              const nextSound = thinkingSounds.find(
                (sound) => !selectedThinkingIds.includes(sound.id),
              );

              if (!nextSound) {
                return;
              }

              updateValue((draft) => ({
                ...draft,
                thinkingSounds: [...draft.thinkingSounds, createThinkingSoundDraft(nextSound)],
              }));
            }}>
            {t('campaignPage.ambienceSettings.addSound')}
          </Button>
        ) : null}
      </Box>
    </Box>
  );
};

export default AmbienceSettingsForm;
